<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
  patchMarketplaceJson,
  postMarketplaceForm,
} from 'dashboard/helpers/marketplaceApi';

// İleriye dönük yayın kanalları (henüz bağlı değil — mimari öngörü).
const CHANNELS = [
  { key: 'shopify_modax', label: 'Shopify · ModaXLarge' },
  { key: 'shopify_adrom', label: 'Shopify · Adrom Toptan' },
  { key: 'trendyol', label: 'Trendyol' },
  { key: 'hepsiburada', label: 'Hepsiburada' },
  { key: 'amazon', label: 'Amazon' },
];

const FIELD_OPTIONS = [
  { value: 'title', label: 'Ürün Başlığı' },
  { value: 'type', label: 'Ürün Tipi' },
  { value: 'vendor', label: 'Marka' },
  { value: 'tag', label: 'Etiket' },
  { value: 'price', label: 'Fiyat' },
  { value: 'status', label: 'Durum' },
];

const TEXT_RELATIONS = [
  { value: 'contains', label: 'içerir' },
  { value: 'not_contains', label: 'içermez' },
  { value: 'equals', label: 'eşittir' },
  { value: 'starts_with', label: 'ile başlar' },
  { value: 'ends_with', label: 'ile biter' },
];
const PRICE_RELATIONS = [
  { value: 'gt', label: 'büyüktür' },
  { value: 'lt', label: 'küçüktür' },
  { value: 'equals', label: 'eşittir' },
];
const TAG_RELATIONS = [
  { value: 'contains', label: 'içerir' },
  { value: 'not_contains', label: 'içermez' },
];

function relationsFor(field) {
  if (field === 'price') return PRICE_RELATIONS;
  if (field === 'tag') return TAG_RELATIONS;
  if (field === 'status') return [{ value: 'equals', label: 'eşittir' }];
  return TEXT_RELATIONS;
}

const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const collectionId = computed(() => route.params.id || null);
const isEdit = computed(() => !!collectionId.value);

const saving = ref(false);
const error = ref('');

const title = ref('');
const handle = ref('');
const description = ref('');
const status = ref('draft');
const image = ref('');
const uploadingImage = ref(false);
const collectionType = ref('manual');
const sortOrder = ref('manual');

const rules = reactive({ match: 'all', conditions: [] });
const productIds = ref([]);
const seo = reactive({ title: '', description: '', handle: '' });

const allProducts = ref([]); // ürün merkezi ürünleri (manuel seçim için)
const productSearch = ref('');
const preview = reactive({ total: 0, products: [], loading: false });

// ── Yardımcılar ──────────────────────────────────────────────────────────
function slugify(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
function onTitleBlur() {
  if (!handle.value) handle.value = slugify(title.value);
  if (!seo.handle) seo.handle = handle.value || slugify(title.value);
  if (!seo.title) seo.title = title.value;
}

async function uploadImage(e) {
  const file = e.target.files && e.target.files[0];
  if (!file) return;
  uploadingImage.value = true;
  try {
    const fd = new FormData();
    fd.append('file', file);
    const res = await postMarketplaceForm(
      accountId,
      'product_center/upload',
      fd
    );
    if (res.url) image.value = res.url;
  } catch (err) {
    error.value = 'Görsel yüklenemedi: ' + err.message;
  } finally {
    uploadingImage.value = false;
    e.target.value = '';
  }
}

// Akıllı kurallar
function addCondition() {
  rules.conditions.push({ field: 'tag', relation: 'contains', value: '' });
}
function removeCondition(idx) {
  rules.conditions.splice(idx, 1);
}
function onFieldChange(cond) {
  const rels = relationsFor(cond.field);
  if (!rels.find(r => r.value === cond.relation)) cond.relation = rels[0].value;
}

// Manuel seçim
const selectedProducts = computed(() => {
  const byId = {};
  allProducts.value.forEach(p => {
    byId[p.id] = p;
  });
  return productIds.value.map(id => byId[id]).filter(Boolean);
});
const availableProducts = computed(() => {
  const q = productSearch.value.trim().toLowerCase();
  return allProducts.value
    .filter(p => !productIds.value.includes(p.id))
    .filter(p => !q || p.title?.toLowerCase().includes(q))
    .slice(0, 40);
});
function addProduct(p) {
  if (!productIds.value.includes(p.id)) productIds.value.push(p.id);
}
function removeProduct(id) {
  productIds.value = productIds.value.filter(x => x !== id);
}

function buildPayload() {
  return {
    title: title.value,
    handle: handle.value || slugify(title.value),
    description: description.value,
    status: status.value,
    collectionType: collectionType.value,
    image: image.value,
    sortOrder: sortOrder.value,
    rules: { match: rules.match, conditions: rules.conditions },
    productIds: productIds.value,
    seo: { ...seo },
  };
}

let previewTimer = null;
async function refreshPreview() {
  if (collectionType.value !== 'smart') return;
  preview.loading = true;
  try {
    const res = await postMarketplaceJsonBody(
      accountId,
      'collections/preview',
      {
        collection: buildPayload(),
      }
    );
    preview.total = res.total || 0;
    preview.products = res.products || [];
  } catch (err) {
    error.value = 'Önizleme hatası: ' + err.message;
  } finally {
    preview.loading = false;
  }
}
function schedulePreview() {
  clearTimeout(previewTimer);
  previewTimer = setTimeout(refreshPreview, 400);
}

watch(
  () => [collectionType.value, rules.match, JSON.stringify(rules.conditions)],
  schedulePreview
);

async function save() {
  if (!title.value.trim()) {
    error.value = 'Koleksiyon adı zorunlu.';
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    if (isEdit.value) {
      await patchMarketplaceJson(
        accountId,
        `collections/${collectionId.value}`,
        {
          collection: buildPayload(),
        }
      );
    } else {
      await postMarketplaceJsonBody(accountId, 'collections', {
        collection: buildPayload(),
      });
    }
    router.push({ name: 'collections_index', params: { accountId } });
  } catch (err) {
    error.value = 'Kaydedilemedi: ' + err.message;
  } finally {
    saving.value = false;
  }
}
function cancel() {
  router.push({ name: 'collections_index', params: { accountId } });
}

async function loadProducts() {
  try {
    const data = await fetchMarketplaceJson(accountId, 'product_center');
    allProducts.value = data.products || [];
  } catch {
    allProducts.value = [];
  }
}

async function loadExisting() {
  if (!isEdit.value) return;
  try {
    const { collection: c } = await fetchMarketplaceJson(
      accountId,
      `collections/${collectionId.value}`
    );
    title.value = c.title || '';
    handle.value = c.handle || '';
    description.value = c.description || '';
    status.value = c.status || 'draft';
    image.value = c.image || '';
    collectionType.value = c.collectionType || 'manual';
    sortOrder.value = c.sortOrder || 'manual';
    rules.match = (c.rules && c.rules.match) || 'all';
    rules.conditions = (c.rules && c.rules.conditions) || [];
    productIds.value = c.productIds || [];
    Object.assign(seo, c.seo || {});
  } catch (err) {
    error.value = 'Koleksiyon yüklenemedi: ' + err.message;
  }
}

onMounted(async () => {
  await loadProducts();
  await loadExisting();
  refreshPreview();
});
</script>

<template>
  <div
    class="flex flex-col h-full flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-3 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <div class="flex items-center gap-3 min-w-0">
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
          @click="cancel"
        >
          <span class="i-lucide-arrow-left size-3.5" />
          Vazgeç
        </button>
        <h1
          class="text-base font-semibold text-slate-900 dark:text-slate-100 truncate"
        >
          {{ isEdit ? 'Koleksiyonu Düzenle' : 'Yeni Koleksiyon' }}
        </h1>
      </div>
      <button
        class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50"
        :disabled="saving"
        @click="save"
      >
        <span
          :class="saving ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-check'"
          class="size-4"
        />
        {{ saving ? 'Kaydediliyor…' : 'Kaydet' }}
      </button>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div class="max-w-6xl mx-auto px-6 py-6">
        <div
          v-if="error"
          class="mb-4 flex items-center gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm"
        >
          <span class="i-lucide-alert-circle size-4 shrink-0" />{{ error }}
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Sol kolon -->
          <div class="lg:col-span-2 space-y-6">
            <!-- Temel -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Koleksiyon Adı
              </label>
              <input
                v-model="title"
                type="text"
                placeholder="Örn. Yaz Koleksiyonu, Büyük Beden, İndirimdekiler"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
                @blur="onTitleBlur"
              />
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mt-4 mb-1"
              >
                Açıklama
              </label>
              <textarea
                v-model="description"
                rows="4"
                placeholder="Koleksiyon açıklaması (vitrin metni, SEO'ya da katkı sağlar)…"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
              />
            </section>

            <!-- Koleksiyon tipi -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Ürünler
              </h2>
              <div class="flex gap-2 mb-4">
                <button
                  class="flex-1 px-3 py-2 rounded-lg border text-sm text-left transition"
                  :class="
                    collectionType === 'manual'
                      ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30'
                      : 'border-slate-200 dark:border-slate-700'
                  "
                  @click="collectionType = 'manual'"
                >
                  <span
                    class="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-1.5"
                  >
                    <span class="i-lucide-hand size-4" /> Manuel
                  </span>
                  <span class="text-xs text-slate-400"
                    >Ürünleri tek tek seç</span
                  >
                </button>
                <button
                  class="flex-1 px-3 py-2 rounded-lg border text-sm text-left transition"
                  :class="
                    collectionType === 'smart'
                      ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30'
                      : 'border-slate-200 dark:border-slate-700'
                  "
                  @click="collectionType = 'smart'"
                >
                  <span
                    class="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-1.5"
                  >
                    <span class="i-lucide-wand-2 size-4" /> Akıllı
                  </span>
                  <span class="text-xs text-slate-400"
                    >Kurala uyanlar otomatik girer</span
                  >
                </button>
              </div>

              <!-- Akıllı kurallar -->
              <div v-if="collectionType === 'smart'">
                <div class="flex items-center gap-2 text-sm mb-3">
                  <span class="text-slate-600 dark:text-slate-300"
                    >Ürünler şu koşulların</span
                  >
                  <select
                    v-model="rules.match"
                    class="px-2 py-1 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                  >
                    <option value="all">tümüne</option>
                    <option value="any">herhangi birine</option>
                  </select>
                  <span class="text-slate-600 dark:text-slate-300">uymalı</span>
                </div>

                <div
                  v-for="(cond, idx) in rules.conditions"
                  :key="idx"
                  class="flex items-center gap-2 mb-2"
                >
                  <select
                    v-model="cond.field"
                    class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                    @change="onFieldChange(cond)"
                  >
                    <option
                      v-for="f in FIELD_OPTIONS"
                      :key="f.value"
                      :value="f.value"
                    >
                      {{ f.label }}
                    </option>
                  </select>
                  <select
                    v-model="cond.relation"
                    class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                  >
                    <option
                      v-for="r in relationsFor(cond.field)"
                      :key="r.value"
                      :value="r.value"
                    >
                      {{ r.label }}
                    </option>
                  </select>
                  <input
                    v-model="cond.value"
                    type="text"
                    placeholder="değer"
                    class="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                  />
                  <button
                    class="text-red-500 p-1"
                    @click="removeCondition(idx)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
                <button
                  class="text-xs text-woot-600 font-medium flex items-center gap-1 mt-1"
                  @click="addCondition"
                >
                  <span class="i-lucide-plus size-3.5" /> Koşul ekle
                </button>

                <!-- Canlı önizleme -->
                <div class="mt-4 rounded-xl bg-slate-50 dark:bg-slate-800 p-3">
                  <div class="flex items-center justify-between mb-2">
                    <span
                      class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide"
                    >
                      Eşleşen ürünler
                    </span>
                    <span
                      class="text-xs text-slate-400 flex items-center gap-1"
                    >
                      <span
                        v-if="preview.loading"
                        class="i-lucide-loader-2 size-3 animate-spin"
                      />
                      {{ preview.total }} ürün
                    </span>
                  </div>
                  <div
                    v-if="preview.products.length"
                    class="flex flex-wrap gap-2"
                  >
                    <div
                      v-for="p in preview.products"
                      :key="p.id"
                      class="size-12 rounded-lg overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
                      :title="p.title"
                    >
                      <img
                        v-if="p.image"
                        :src="p.image"
                        class="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <p v-else class="text-xs text-slate-400">
                    Henüz eşleşen ürün yok.
                  </p>
                </div>
              </div>

              <!-- Manuel seçim -->
              <div v-else>
                <div v-if="selectedProducts.length" class="space-y-2 mb-4">
                  <div
                    v-for="p in selectedProducts"
                    :key="p.id"
                    class="flex items-center gap-3 p-2 rounded-lg border border-slate-200 dark:border-slate-700"
                  >
                    <div
                      class="size-9 rounded overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0"
                    >
                      <img
                        v-if="p.image"
                        :src="p.image"
                        class="w-full h-full object-cover"
                      />
                    </div>
                    <span
                      class="text-sm text-slate-700 dark:text-slate-200 truncate flex-1"
                    >
                      {{ p.title }}
                    </span>
                    <button
                      class="text-red-500 p-1"
                      @click="removeProduct(p.id)"
                    >
                      <span class="i-lucide-x size-4" />
                    </button>
                  </div>
                </div>

                <div
                  class="flex items-center gap-2 mb-2 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-woot-500"
                >
                  <span
                    class="i-lucide-search size-4 shrink-0 text-slate-400"
                  />
                  <input
                    v-model="productSearch"
                    type="text"
                    placeholder="Ürün merkezinden ürün ara ve ekle…"
                    class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm !bg-transparent !border-0 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                  />
                </div>
                <div
                  class="max-h-56 overflow-y-auto rounded-lg border border-slate-100 dark:border-slate-800"
                >
                  <button
                    v-for="p in availableProducts"
                    :key="p.id"
                    class="w-full flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 text-left"
                    @click="addProduct(p)"
                  >
                    <div
                      class="size-8 rounded overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0"
                    >
                      <img
                        v-if="p.image"
                        :src="p.image"
                        class="w-full h-full object-cover"
                      />
                    </div>
                    <span
                      class="text-sm text-slate-600 dark:text-slate-300 truncate flex-1"
                    >
                      {{ p.title }}
                    </span>
                    <span class="i-lucide-plus size-4 text-slate-400" />
                  </button>
                  <p
                    v-if="!availableProducts.length"
                    class="text-xs text-slate-400 p-3 text-center"
                  >
                    Ürün merkezinde eklenebilecek ürün yok.
                  </p>
                </div>
              </div>
            </section>

            <!-- SEO -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Arama Motoru (SEO)
              </h2>
              <label class="block text-xs text-slate-500 mb-1"
                >Sayfa Başlığı</label
              >
              <input
                v-model="seo.title"
                type="text"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />
              <label class="block text-xs text-slate-500 mb-1"
                >Meta Açıklama</label
              >
              <textarea
                v-model="seo.description"
                rows="2"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />
              <label class="block text-xs text-slate-500 mb-1"
                >URL Uzantısı (handle)</label
              >
              <input
                v-model="seo.handle"
                type="text"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              />
            </section>
          </div>

          <!-- Sağ kolon -->
          <div class="space-y-6">
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Durum
              </h2>
              <select
                v-model="status"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              >
                <option value="draft">Taslak</option>
                <option value="active">Aktif</option>
              </select>

              <label class="block text-xs text-slate-500 mt-4 mb-1"
                >Ürün Sıralaması</label
              >
              <select
                v-model="sortOrder"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              >
                <option value="manual">Manuel</option>
                <option value="alpha-asc">İsme göre (A→Z)</option>
                <option value="alpha-desc">İsme göre (Z→A)</option>
                <option value="price-asc">Fiyat (artan)</option>
                <option value="price-desc">Fiyat (azalan)</option>
                <option value="created-desc">En yeni</option>
              </select>
            </section>

            <!-- Kapak görseli -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Kapak Görseli
              </h2>
              <div
                v-if="image"
                class="relative aspect-[3/2] rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 mb-2"
              >
                <img :src="image" class="w-full h-full object-cover" />
                <button
                  class="absolute top-2 right-2 size-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                  @click="image = ''"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>
              <label
                class="flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 cursor-pointer hover:border-woot-400"
              >
                <span
                  :class="
                    uploadingImage
                      ? 'i-lucide-loader-2 animate-spin'
                      : 'i-lucide-image-plus'
                  "
                  class="size-4"
                />
                {{ image ? 'Değiştir' : 'Görsel yükle' }}
                <input
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="uploadImage"
                />
              </label>
            </section>

            <!-- Yayın kanalları (ileriye dönük) -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-1"
              >
                Yayın Kanalları
              </h2>
              <p class="text-xs text-slate-400 mb-3">
                Bu koleksiyon ileride buradan tüm pazaryerlerine yayınlanacak.
              </p>
              <div class="space-y-1.5">
                <div
                  v-for="ch in CHANNELS"
                  :key="ch.key"
                  class="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800"
                >
                  <span class="text-sm text-slate-600 dark:text-slate-300">{{
                    ch.label
                  }}</span>
                  <span
                    class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
                  >
                    Yakında
                  </span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
